/**
 * SupplierJourneyContextContract (AJIP-16.02.008 · WS9)
 * ---------------------------------------------------------------------
 * Validation of supplier-specific context passed through orchestration
 * boundaries. Framework-agnostic. Deterministic. No side effects.
 *
 * Contract:
 *   - Plain object OR null / undefined.
 *   - Serialisable.
 *   - Byte-capped (8 KB).
 *   - No forbidden keys (auth tokens, credentials, payment info, etc).
 *   - No embedded executable data.
 *
 * Note: Organisation reference MAY appear as `organisationRef` in context.
 * Ownership / authority / permissions MUST NOT appear inside context —
 * they are evaluated at runtime via SupplierJourneyOwnership (S7).
 */
import { InvalidSupplierContextError } from './SupplierJourneyErrors';

const MAX_SUPPLIER_CONTEXT_BYTES = 8 * 1024;

const FORBIDDEN_KEYS = Object.freeze([
  'password', 'token', 'apikey', 'secret', 'credential',
  'cardnumber', 'cvv', 'privatekey', 'sessionid',
  'accesstoken', 'refreshtoken', 'bearer', 'authorization',
  'providersession', 'paymentinfo',
]);

const isPlainObject = (v) => (
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null)
);

const walkForbidden = (value, path = 'context') => {
  if (value === null || value === undefined) return;
  if (typeof value === 'function') {
    throw new InvalidSupplierContextError(`Executable data at ${path} prohibited`);
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) => walkForbidden(item, `${path}[${i}]`));
    return;
  }
  if (typeof value !== 'object') return;
  if (!isPlainObject(value)) {
    throw new InvalidSupplierContextError(`${path} must be plain object`);
  }
  for (const [k, v] of Object.entries(value)) {
    if (FORBIDDEN_KEYS.includes(k.toLowerCase())) {
      throw new InvalidSupplierContextError(`Forbidden key '${k}' at ${path}`);
    }
    walkForbidden(v, `${path}.${k}`);
  }
};

const validateSupplierContext = (ctx) => {
  if (ctx === null || ctx === undefined) return true;
  if (!isPlainObject(ctx)) {
    throw new InvalidSupplierContextError('Context must be plain object');
  }
  walkForbidden(ctx);
  let s;
  try { s = JSON.stringify(ctx); } catch (_e) {
    throw new InvalidSupplierContextError('Not serialisable');
  }
  const bytes = typeof Buffer !== 'undefined' ? Buffer.byteLength(s, 'utf8') : s.length;
  if (bytes > MAX_SUPPLIER_CONTEXT_BYTES) {
    throw new InvalidSupplierContextError('Context exceeds byte cap');
  }
  return true;
};

export { validateSupplierContext, MAX_SUPPLIER_CONTEXT_BYTES, FORBIDDEN_KEYS };
